import { makeStyles } from '@material-ui/core/styles';

/**
 * Styles for the Curation Prompt Application floating chat widget.
 *
 * The panel/button rules are copied verbatim from the program-creation chat
 * widget (DQProV2ProgCreatorPreviewStyles) so the look + motion match exactly,
 * but kept here as a standalone stylesheet so the component file holds no CSS.
 */
const useStyles = makeStyles((theme) => ({
    // ── Attention animations ──────────────────────────────────────────────────
    '@keyframes chatShake': {
        '0%, 14%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
        '2%':  { transform: 'translate(-5px, 0) rotate(-4deg)' },
        '4%':  { transform: 'translate(5px, 0) rotate(4deg)' },
        '6%':  { transform: 'translate(-4px, 0) rotate(-2.5deg)' },
        '8%':  { transform: 'translate(4px, 0) rotate(2.5deg)' },
        '10%': { transform: 'translate(-2px, 0) rotate(-1deg)' },
        '12%': { transform: 'translate(0, 0) rotate(0deg)' },
    },
    '@keyframes chatPulseRing': {
        '0%':        { transform: 'scale(1)',   opacity: 0.65 },
        '75%, 100%': { transform: 'scale(2.1)', opacity: 0 },
    },
    chatFloatingPanel: {
        position: 'fixed',
        right: '24px',
        bottom: '24px',
        width: '400px',
        height: 'calc(100vh - 48px)',
        maxHeight: '720px',
        borderRadius: '16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.18)',
        overflow: 'hidden',
        zIndex: 1301,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--brand-surface, #fff)',
        border: '1px solid var(--brand-border, #EBEBEB)',
        willChange: 'clip-path, transform',
        [theme.breakpoints.down('sm')]: {
            right: 0,
            bottom: 0,
            width: '100vw',
            height: '100vh',
            maxHeight: '100vh',
            borderRadius: 0,
        },
        '&.phase-pre-open': {
            clipPath: 'inset(100% 0 0 0 round 16px)',
            transform: 'none',
            transition: 'none',
        },
        '&.phase-opening': {
            clipPath: 'inset(0% 0 0 0 round 16px)',
            transform: 'none',
            transition: 'clip-path 1.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&.phase-sliding, &.phase-open': {
            clipPath: 'inset(0% 0 0 0 round 16px)',
            transform: 'none',
            transition: 'none',
        },
        '&.phase-closing': {
            transform: 'scale(0)',
            transformOrigin: 'calc(100% - 28px) calc(100% - 28px)',
            transition: 'transform 1.44s cubic-bezier(0.4, 0, 0.8, 1)',
        },
    },
    // ── "Already open" attention ring ─────────────────────────────────────────
    // Played when a trigger asks to open a panel that is already open: an
    // accent stroke runs around the panel's border, then fades. Drawn INSIDE
    // the panel (the panel's clip-path would cut anything outside it) as a
    // ring-shaped mask over a rotating conic gradient.
    '@keyframes attentionSpin': {
        from: { transform: 'translate(-50%, -50%) rotate(0deg)' },
        to:   { transform: 'translate(-50%, -50%) rotate(720deg)' },
    },
    '@keyframes attentionFade': {
        '0%':   { opacity: 0 },
        '8%':   { opacity: 1 },
        '75%':  { opacity: 1 },
        '100%': { opacity: 0 },
    },
    attentionRing: {
        position: 'absolute',
        inset: 0,
        zIndex: 2,
        padding: '4px',
        borderRadius: 'inherit',
        overflow: 'hidden',
        pointerEvents: 'none',
        boxShadow: 'inset 0 0 18px rgba(var(--pbdq-accent-rgb, 255, 105, 0), 0.35)',
        WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        WebkitMaskComposite: 'xor',
        mask: 'linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0)',
        animation: '$attentionFade 2.6s ease forwards',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '50%',
            left: '50%',
            // Big enough that its rotation always covers the panel's corners.
            width: '250vmax',
            height: '250vmax',
            background: 'conic-gradient(from 0deg, transparent 0deg, transparent 160deg, var(--pbdq-accent, #FF6900) 280deg, var(--pbdq-accent, #FF6900) 350deg, transparent 360deg)',
            animation: '$attentionSpin 2.6s linear forwards',
        },
        '&::after': {
            // A faint static accent ring so the whole border reads as lit,
            // with the running stroke on top.
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'rgba(var(--pbdq-accent-rgb, 255, 105, 0), 0.3)',
        },
    },
    chatToggleBtn: {
        position: 'fixed',
        right: '24px',
        bottom: '24px',
        width: '56px',
        height: '56px',
        borderRadius: '50%',
        background: 'var(--brand-surface, #fff)',
        border: '2.5px solid var(--pbdq-accent, #FF6900)',
        boxShadow: '0 4px 18px rgba(var(--pbdq-accent-rgb, 255, 105, 0), 0.35)',
        cursor: 'pointer',
        zIndex: 1300,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0px',
        boxSizing: 'border-box',
        willChange: 'transform, opacity',
        '&.btn-idle': {
            opacity: 1,
            pointerEvents: 'auto',
            // Shake once after 1.5 s, then repeat every 5 s
            animation: '$chatShake 5s ease-in-out 1.5s infinite',
            transition: 'box-shadow 0.3s ease',
        },
        // Expanding pulse ring
        '&.btn-idle::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '2px solid rgba(var(--pbdq-accent-rgb, 255, 105, 0), 0.5)',
            animation: '$chatPulseRing 2.6s ease-out 0.4s infinite',
            pointerEvents: 'none',
        },
        // Hover tooltip label
        '&.btn-idle::after': {
            content: '"click to find your perfect package"',
            position: 'absolute',
            right: 'calc(100% + 13px)',
            top: '50%',
            transform: 'translateY(-50%) translateX(8px)',
            background: 'var(--pbdq-accent-tint, #FFF3E9)',
            color: 'var(--pbdq-accent-hover, #CC5500)',
            border: '1px solid #FFD4B3',
            fontSize: 13,
            fontWeight: 600,
            padding: '7px 14px',
            borderRadius: 20,
            whiteSpace: 'nowrap',
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.2s ease, transform 0.2s ease',
            boxShadow: '0 3px 10px rgba(var(--pbdq-accent-rgb, 255, 105, 0), 0.12)',
        },
        '&.btn-idle:hover': {
            boxShadow: '0 6px 22px rgba(var(--pbdq-accent-rgb, 255, 105, 0), 0.6)',
            animationPlayState: 'paused',
            transform: 'scale(1.1)',
        },
        '&.btn-idle:hover::after': {
            opacity: 1,
            transform: 'translateY(-50%) translateX(0)',
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '14px 16px',
        borderBottom: '1px solid var(--brand-border, #F0F0F0)',
        flexShrink: 0,
        minWidth: 0,
        overflow: 'hidden',
    },
    brandIcon: {
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        backgroundColor: 'var(--brand-surface, #fff)',
        border: '2px solid var(--pbdq-accent, #FF6900)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        padding: '2px',
    },
    body: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    closeBtn: {
        width: '28px',
        height: '28px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        background: 'var(--brand-surface, #F3F4F6)',
        flexShrink: 0,
        color: 'var(--pbdq-accent, #FF6900)',
        transition: 'background 0.15s, transform 0.15s',
        border: 'none',
        padding: 0,
        '&:hover': {
            background: 'var(--brand-border, #E5E7EB)',
        },
        [theme.breakpoints.down('sm')]: {
            width: '32px',
            height: '32px',
        },
    },
    minimizeIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    closeIcon: {
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        [theme.breakpoints.down('sm')]: {
            display: 'inline-flex',
        },
    },
}));

export default useStyles;
